import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLogo from "@/components/AppLogo";

const AUTH_BACKGROUND =
  "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1200&q=80";

interface AuthShellProps {
  /**
   * Width of the right-hand column. The login form uses the default `max-w-md`;
   * the signup flow widens it so the 6-step progress indicator fits.
   */
  contentClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared shell for the public auth screens: photographic branding panel on the
 * left (desktop only) and a white form column on the right, with the brand
 * repeated above the form on small screens.
 *
 * Used by the login page and by every signup step via `SignupLayout`.
 */
const AuthShell: React.FC<AuthShellProps> = ({
  contentClassName = "max-w-md",
  children,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen">
      {/* Left panel — branding */}
      <div
        className="relative hidden overflow-hidden bg-gradient-to-br from-[#2b1a05] via-[#7a4a10] to-[#1a1206] bg-cover bg-center lg:flex lg:w-1/2"
        style={{ backgroundImage: `url(${AUTH_BACKGROUND})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-tl from-black/70 via-black/40 to-black/30" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-20 top-20 h-64 w-64 rounded-full bg-[#ffa217]/30 blur-3xl" />
          <div className="absolute bottom-20 right-20 h-80 w-80 rounded-full bg-[#ffa217]/20 blur-3xl" />
        </div>
        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          <Link to="/" className="inline-flex items-center gap-2">
            <AppLogo size={40} />
            <span className="text-2xl font-extrabold tracking-wide text-white">
              Uninfo<span className="text-[#ffa217]">HK</span>
            </span>
          </Link>
          <div>
            <h2 className="mb-4 text-4xl font-extrabold leading-tight text-white">
              {t("Your university life,")}
              <br />
              <span className="text-[#ffa217]">{t("all in one place.")}</span>
            </h2>
            <p className="max-w-md text-lg leading-relaxed text-white/50">
              {t("Discover events, join societies, complete surveys and redeem coupons — built for Hong Kong university students.")}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/30">
            <Link to="/terms" className="transition-colors hover:text-white/60">
              {t("Terms")}
            </Link>
            <span>·</span>
            <Link to="/privacy" className="transition-colors hover:text-white/60">
              {t("Privacy")}
            </Link>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex flex-1 items-center justify-center bg-white px-6 py-12">
        <div className={`w-full ${contentClassName}`}>
          {/* Mobile-only brand */}
          <div className="mb-8 text-center lg:hidden">
            <Link to="/" className="inline-flex items-center gap-2">
              <AppLogo size={36} />
              <span className="text-xl font-extrabold text-[#333]">
                Uninfo<span className="text-[#ffa217]">HK</span>
              </span>
            </Link>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthShell;
